/**
 * P-28b (D-169) - pure helpers of the profitability screens: the period selector,
 * the hours bar, the "negative profit" warning, the business report and the two
 * "my management" bands. No React; unit-tested (`profitabilityModel.test.ts`).
 * The data comes from `/api/profitability/*` (P-28a contract).
 */
import type { Band, RoleBandsData } from '../home/roleBandsModel';
import { bandsFrom } from '../home/roleBandsModel';

// ---------------------------------------------------------------- period

export type ProfitPeriodKind = 'all' | 'month' | 'range';

/** What the person picked in the period selector. */
export interface ProfitPeriod {
  kind: ProfitPeriodKind;
  /** YYYY-MM (kind 'month'). */
  month: string;
  /** YYYY-MM-DD (kind 'range'); either may be empty. */
  from: string;
  to: string;
}

export interface ProfitPeriodParams {
  month?: string;
  from?: string;
  to?: string;
  range?: 'all';
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** This month in Israel as YYYY-MM. */
export function currentMonthIsrael(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit' }).format(now).slice(0, 7);
}

/** A project starts "from the start" (the default); a customer starts at this month. */
export function defaultPeriod(kind: 'all' | 'month', now: Date = new Date()): ProfitPeriod {
  return { kind, month: currentMonthIsrael(now), from: '', to: '' };
}

/** A month or a range that cannot be sent (bad format, or the range runs backwards). */
export function periodError(p: ProfitPeriod): 'month' | 'range' | 'order' | null {
  if (p.kind === 'month') return MONTH_RE.test(p.month) ? null : 'month';
  if (p.kind === 'range') {
    if (!p.from && !p.to) return 'range';
    if ((p.from && !DAY_RE.test(p.from)) || (p.to && !DAY_RE.test(p.to))) return 'range';
    if (p.from && p.to && p.from > p.to) return 'order';
  }
  return null;
}

/**
 * The query the API gets. "From the start" sends `range=all` (explicit). An invalid
 * choice gives null (do not fetch).
 */
export function periodParams(p: ProfitPeriod): ProfitPeriodParams | null {
  if (periodError(p)) return null;
  if (p.kind === 'all') return { range: 'all' };
  if (p.kind === 'month') return { month: p.month };
  return { ...(p.from ? { from: p.from } : {}), ...(p.to ? { to: p.to } : {}) };
}

/**
 * BQ-040 (Q-148) - a date range is calculated in WHOLE months (cost and revenue on the
 * same months), as the server does. Returns the effective range when it differs from
 * what was picked, else null. Dates are YYYY-MM-DD.
 */
export function wholeMonthsRange(p: ProfitPeriod): { from: string; to: string } | null {
  if (p.kind !== 'range' || periodError(p)) return null;
  const first = (d: string) => `${d.slice(0, 7)}-01`;
  const last = (d: string) => {
    const dim = new Date(Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)), 0)).getUTCDate();
    return `${d.slice(0, 7)}-${String(dim).padStart(2, '0')}`;
  };
  const from = p.from ? first(p.from) : '';
  const to = p.to ? last(p.to) : '';
  return from === p.from && to === p.to ? null : { from, to };
}

/** "31/10" style day.month for the whole-months notice. */
export function dayMonth(d: string): string {
  return d ? `${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}` : '';
}

/** A stable key for react-query. */
export function periodKey(p: ProfitPeriod): string {
  if (p.kind === 'all') return 'all';
  if (p.kind === 'month') return `m:${p.month}`;
  return `r:${p.from}:${p.to}`;
}

// ------------------------------------------------------------ hours bar

export type BarTone = 'ok' | 'warn' | 'danger';

export interface HoursBarModel {
  /** Actual / planned in percent (can pass 100); null without a plan. */
  pct: number | null;
  /** Width of the filled part, 0..100. */
  fill: number;
  tone: BarTone;
}

/** Orange from 80% of the plan, red above 100% (D-169). */
export function hoursBarModel(actual: number | null | undefined, planned: number | null | undefined): HoursBarModel {
  const a = Number(actual ?? 0);
  const p = Number(planned ?? 0);
  if (!(p > 0)) return { pct: null, fill: 0, tone: 'ok' };
  const pct = (a / p) * 100;
  const tone: BarTone = pct > 100 ? 'danger' : pct >= 80 ? 'warn' : 'ok';
  return { pct, fill: Math.max(0, Math.min(100, pct)), tone };
}

// ------------------------------------------------------- project profit

export interface ProjectPlan {
  planned_hours: number | null;
  actual_hours: number | null;
  deviation_hours: number | null;
  deviation_pct: number | null;
  planned_cost: number | null;
  actual_cost: number | null;
  deviation_cost: number | null;
  planned_profit: number | null;
  expected_profit: number | null;
}

export interface ProjectEmployeeRow {
  user_id: string;
  name: string;
  hours: number;
  billable_hours: number;
  /** Only the OTE manager gets it. */
  cost?: number;
  /** BQ-011: fixed-price project, cost-viewers only. */
  revenue?: number;
  profit?: number;
  profit_per_hour?: number | null;
}

export interface PeriodBlock {
  kind: ProfitPeriodKind;
  month: string | null;
  from: string | null;
  to: string | null;
}

export interface ProjectProfit {
  project_id: string;
  period: PeriodBlock;
  price: number | null;
  revenue: number;
  revenue_to_date: number;
  hours: number;
  labour_cost: number;
  ai_cost: number;
  /** P-34 (D-184): the server - its cost to OTE (inside `cost`), what the customer paid for it (inside `revenue`) and its profit. */
  server_cost?: number;
  server_revenue?: number;
  server_profit?: number;
  cost: number;
  profit: number;
  margin: number | null;
  revenue_per_hour: number | null;
  profit_per_hour: number | null;
  negative_profit: boolean;
  plan: ProjectPlan | null;
  by_employee: ProjectEmployeeRow[];
  /** DQ-062 (D-109): "X עובדים בלי עלות. הרווחיות לא מדויקת", like the other profitability views. */
  cost_warning?: { employees_missing_cost: number };
}

/**
 * The "רווח שלילי" warning at the top of the project (D-169). T-46: a project whose
 * price is 0 (hourly, no fixed price) never gets it. The server already leaves the flag
 * off then; this guards the screen against any other answer.
 */
export function showNegativeProfitWarning(p: Pick<ProjectProfit, 'negative_profit' | 'price'> | null | undefined): boolean {
  if (!p || p.negative_profit !== true) return false;
  return Number(p.price ?? 0) > 0;
}

/** Whether the project page offers the "רווחיות" tab at all: only who sees money (D-15, D-95). */
export function showProfitTab(canViewFinancials: boolean | undefined | null): boolean {
  return canViewFinancials === true;
}

/** The employee split, biggest first; the cost column exists only when the server sent a cost. */
export function employeeRows(rows: ProjectEmployeeRow[]): { rows: ProjectEmployeeRow[]; showCost: boolean; showProfit: boolean } {
  const sorted = [...rows].sort((a, b) => b.hours - a.hours);
  return {
    rows: sorted,
    showCost: sorted.some((r) => typeof r.cost === 'number'),
    showProfit: sorted.some((r) => typeof r.revenue === 'number'),
  };
}

/** "Planned vs expected profit" with the gap; all null without a plan. */
export function profitOutlook(plan: ProjectPlan | null): { planned: number | null; expected: number | null; gap: number | null } {
  const planned = plan?.planned_profit ?? null;
  const expected = plan?.expected_profit ?? null;
  return { planned, expected, gap: planned != null && expected != null ? expected - planned : null };
}

// ------------------------------------------------------- customer profit

export interface CustomerProfit {
  organization_id: string;
  name: string;
  is_internal: boolean;
  period: PeriodBlock;
  hours: number;
  billable_hours: number;
  revenue: number | null;
  labour_cost: number;
  ai_cost: number;
  /** P-34 (D-184): the server's cost / payment / profit (null revenue + profit for an internal customer). */
  server_cost?: number;
  server_revenue?: number | null;
  server_profit?: number | null;
  cost: number;
  profit: number | null;
  margin: number | null;
  revenue_per_hour: number | null;
  profit_per_hour: number | null;
}

export type CustomerFigure = 'hours' | 'revenue' | 'cost' | 'profit' | 'revenuePerHour';

/** Which numbers the customer summary shows: an internal customer shows hours and cost only. */
export function customerSummaryKeys(c: Pick<CustomerProfit, 'is_internal'>): CustomerFigure[] {
  return c.is_internal ? ['hours', 'cost'] : ['hours', 'revenue', 'cost', 'profit', 'revenuePerHour'];
}

// ------------------------------------------------------- business report

export type BusinessType =
  | 'project' | 'development' | 'service_contract' | 'hour_bank'
  | 'service_without_contract' | 'characterization' | 'internal' | 'other';

export interface BusinessProfit {
  month: string;
  revenue: number;
  labour_cost: number;
  ai_cost: number;
  /** P-50 (D-222): operating expenses from the cash-flow page (inside `cost`), and their split by category. */
  operating_expenses?: number;
  operating_by_category?: Array<{ category: string; amount: number }>;
  /** P-34 (D-184): the servers' cost to OTE (inside `cost`), what customers paid for them (inside `revenue`) and their profit. */
  server_cost?: number;
  server_revenue?: number;
  server_profit?: number;
  cost: number;
  profit: number;
  margin: number | null;
  hours: number;
  billable_hours: number;
  revenue_per_hour: number | null;
  profit_per_hour: number | null;
  series: Array<{ month: string; revenue: number; cost: number; profit: number }>;
  by_type: Array<{ type: BusinessType; revenue: number; cost: number; profit: number }>;
  /** PM-18: not the full report yet (the (i) next to the title). */
  incomplete: boolean;
  cost_warning?: { employees_missing_cost: number };
}

export type BusinessFigureKey = 'revenue' | 'labourCost' | 'aiCost' | 'profit';

/**
 * The four numbers of the month (PM-18: revenue, the cost of the hours, AI cost, profit),
 * in order; the profit margin and the revenue per hour are shown next to them.
 */
export function businessFigures(b: Pick<BusinessProfit, 'revenue' | 'labour_cost' | 'ai_cost' | 'profit'>): Array<{ key: BusinessFigureKey; value: number }> {
  return [
    { key: 'revenue', value: b.revenue },
    { key: 'labourCost', value: b.labour_cost },
    { key: 'aiCost', value: b.ai_cost },
    { key: 'profit', value: b.profit },
  ];
}

/**
 * P-34 (D-184, D-188) - the "שרת" line: what the customer pays, what it costs OTE and the profit.
 * null when there is no server in the figures (nothing is drawn).
 */
export interface ServerLine { revenue: number; cost: number; profit: number }
export function serverLine(x: { server_revenue?: number | null; server_cost?: number | null; server_profit?: number | null } | null | undefined): ServerLine | null {
  const revenue = Number(x?.server_revenue ?? 0) || 0;
  const cost = Number(x?.server_cost ?? 0) || 0;
  if (revenue === 0 && cost === 0) return null;
  return { revenue, cost, profit: x?.server_profit != null ? Number(x.server_profit) : Math.round((revenue - cost) * 100) / 100 };
}

/** A ratio (0.25) as "25.0%"; null as a dash. */
export function marginText(margin: number | null | undefined): string {
  return margin == null || !Number.isFinite(Number(margin)) ? '—' : `${(Number(margin) * 100).toFixed(1)}%`;
}

/** By engagement type, largest revenue first (then largest cost), at most `limit` rows. */
export function businessTypeRows(rows: BusinessProfit['by_type'], limit = 6): BusinessProfit['by_type'] {
  return [...rows].sort((a, b) => (b.revenue - a.revenue) || (b.cost - a.cost)).slice(0, limit);
}

/**
 * P-50 (D-222): the "הוצאות תפעוליות" line of the month: the total and the category split for the
 * tooltip (largest first). null when there are none (nothing is drawn).
 */
export function operatingLine(b: Pick<BusinessProfit, 'operating_expenses' | 'operating_by_category'> | null | undefined): { total: number; rows: Array<{ category: string; amount: number }> } | null {
  const total = Number(b?.operating_expenses ?? 0) || 0;
  if (total === 0) return null;
  return { total, rows: [...(b?.operating_by_category ?? [])].sort((a, c) => c.amount - a.amount) };
}

/** The 12 months of the chart, oldest first. */
export function profitSeries(series: BusinessProfit['series']): Array<{ month: string; profit: number }> {
  return [...series].sort((a, b) => a.month.localeCompare(b.month)).map((s) => ({ month: s.month, profit: s.profit }));
}

/** The OTE manager's: the General Manager (the server also asks for the profitability permission). */
export function showBusinessReport(isGeneralManager: boolean | undefined | null): boolean {
  return isGeneralManager === true;
}

/** The card on the dashboard (`admin-summary.monthly_business`); null for anyone else. */
export interface MonthlyBusiness {
  month: string;
  revenue: number;
  cost: number;
  profit: number;
  /** P-34 (D-184): the servers' profit this month. */
  server_profit?: number;
  hours: number;
  revenue_per_hour: number | null;
  profit_per_hour: number | null;
}

// ------------------------------------------------------ "my management"

/**
 * QX-106: the OTE manager who is also a customer manager and/or a project manager sees
 * "הלקוחות שלי" and "הפרויקטים שלי" under the general strip. Without duplicates of it:
 * no team band (the strip is the whole team's), and no "projects at risk" cell (the
 * strip names the projects at risk). A band left without cells is dropped.
 */
export function ownManagementBands(data: RoleBandsData | null | undefined): Band[] {
  return bandsFrom(data)
    .filter((b) => b.id !== 'team')
    .map((b) => (b.id === 'projects' ? { ...b, cells: b.cells.filter((c) => c.key !== 'projectsAtRisk') } : b))
    .filter((b) => b.cells.length > 0);
}

// ------------------------------------------------------ P-34: the servers report

export interface ServersReportRow {
  organization_id: string;
  organization_name: string | null;
  engagement_id: string | null;
  engagement_name: string | null;
  project_id: string | null;
  type: 'project' | 'development' | 'service_contract' | 'hour_bank' | 'service_without_contract';
  fee: number;
  cost: number;
  profit: number;
  missing_cost: boolean;
  missing_fee: boolean;
  cost_over_price: boolean;
}

export interface ServersReport {
  month: string;
  rows: ServersReportRow[];
  totals: { fee: number; cost: number; profit: number };
}

/** The flag to show next to a server row (the most important one first), or null. */
export function serverRowFlag(r: Pick<ServersReportRow, 'missing_cost' | 'missing_fee' | 'cost_over_price'>): 'missingCost' | 'missingFee' | 'costOverPrice' | null {
  if (r.missing_cost) return 'missingCost';
  if (r.cost_over_price) return 'costOverPrice';
  if (r.missing_fee) return 'missingFee';
  return null;
}

/** HN-1: the link from "no plan" to the project's edit form (ProjectPage opens it on `?edit=1`), where the planned hours are set. */
export function projectPlanSettingHref(projectId: string): string {
  return `/projects/${encodeURIComponent(projectId)}?edit=1`;
}
